import React, { useState, useEffect } from "react";
import { getAllAgent } from "rc-app/api/agentActionsApi";

interface Agent {
  _id: string;
  first_name: string;
  last_name: string;
  agent_id: string;
  email_id: string;
}

interface AssignAgentModalProps {
  agencyId: string;
  /** When set, restrict the picker to these agent _ids (e.g. an ASA's own team). */
  allowedAgentIds?: string[];
  onClose: () => void;
  onAssign: (agentMongoId: string) => void;
}

const AssignAgentModal: React.FC<AssignAgentModalProps> = ({
  agencyId,
  allowedAgentIds,
  onClose,
  onAssign,
}) => {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [selectedAgentId, setSelectedAgentId] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    try {
      setLoading(true);
      const data = await getAllAgent();
      const agencyAgents = data?.re_agent_list?.filter(
        (agent: any) => agent.agency?.["_id"] === agencyId
      ) || [];
      setAgents(agencyAgents);
    } catch (error) {
      console.error("Failed to fetch agents:", error);
    } finally {
      setLoading(false);
    }
  };

  const allowedSet = allowedAgentIds ? new Set(allowedAgentIds) : null;
  const filteredAgents = agents
    .filter((agent) => !allowedSet || allowedSet.has(agent._id))
    .filter((agent) => {
      const query = search.toLowerCase();
      return (
        agent.first_name?.toLowerCase().includes(query) ||
        agent.last_name?.toLowerCase().includes(query) ||
        agent.agent_id?.toLowerCase().includes(query) ||
        agent.email_id?.toLowerCase().includes(query)
      );
    });

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-[#2E4051] text-white rounded-lg p-6 max-w-lg w-full mx-4"
      >
        <h2 className="text-xl font-semibold mb-4">Assign Agent</h2>

        <input
          type="text"
          placeholder="Search agent..."
          className="w-full px-3 py-2 mb-4 text-sm text-white bg-[#1C2C3A] rounded-md focus:outline-none focus:ring-0 focus:ring-[#008564]"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <div className="max-h-72 overflow-y-auto scrollbar-hide">
          {loading ? (
            <div className="text-center py-6 text-gray-400">Loading agents...</div>
          ) : filteredAgents.length === 0 ? (
            <div className="text-center py-6 text-gray-400">No agents found</div>
          ) : (
            filteredAgents.map((agent) => (
              <div
                key={agent._id}
                onClick={() => setSelectedAgentId(agent._id)}
                className={`flex items-center gap-3 px-3 py-3 border-b border-[#374B5E] cursor-pointer transition-colors ${
                  selectedAgentId === agent._id
                    ? "bg-[#35495E]"
                    : "hover:bg-[#35495E]"
                }`}
              >
                <div
                  className={`flex h-5 w-5 border-2 border-white rounded items-center justify-center ${
                    selectedAgentId === agent._id ? "bg-[#008564]" : ""
                  }`}
                >
                  {selectedAgentId === agent._id && (
                    <svg
                      className="w-3 h-3 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={3}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  )}
                </div>
                <div className="flex-1">
                  <div className="font-semibold">
                    {agent.first_name} {agent.last_name}
                  </div>
                  <div className="text-sm text-gray-400">
                    {agent.agent_id} • {agent.email_id}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onClose}
            className="bg-gray-700 px-6 py-2 rounded"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              if (selectedAgentId) {
                onAssign(selectedAgentId);
              }
            }}
            disabled={!selectedAgentId}
            className={`px-6 py-2 rounded ${
              selectedAgentId
                ? "bg-green-600 hover:bg-green-700"
                : "bg-gray-600 cursor-not-allowed opacity-50"
            }`}
          >
            Assign
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignAgentModal;
